import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import moment from 'moment';
import 'moment/locale/es';

import { getEmissionsSummary } from '../../../../../slices/thunks';
import OChartCard from '../../../../../Components/OElements/OChartCard';
import OBarChart from '../../../../../Components/OElements/OBarChart';

moment.locale('es');

// ─── Helpers ────────────────────────────────────────────────────────────

const formatMap = {
	'2A': 'YYYY',
	'1A': 'YYYY',
	'6M': 'MM-YYYY',
	'3M': 'MM-YYYY',
	'1M': 'YYYY-[S]WW',
	'7D': 'DD-MM-YYYY',
	AC: 'MM-YYYY',
};

const formatDate = (date, filter) => {
	return moment(date).format(formatMap[filter] || 'DD-MM-YYYY');
};

// const getUniqueUnits = data => {
// 	if (!Array.isArray(data) || data.length === 0) return 'N/A';
// 	const uniqueUnits = new Set(
// 		data
// 			.map(item => 'kgCO2')
// 			.filter(Boolean)
// 			.map(unit => unit.charAt(0).toUpperCase() + unit.slice(1))
// 	);
// 	return uniqueUnits.size > 0 ? Array.from(uniqueUnits).join(', ') : 'N/A';
// };

const EmissionsPowerGenerationEChart = ({
	establishmentId,
	title,
	legendPosition = 'bottom',
	defaultFilters = {
		option: 'dia',
		group: 'centro',
	},
	um = '',
	minHeight,
	maxHeight,
}) => {
	const dispatch = useDispatch();
	const [selectedDate, setSelectedDate] = useState(defaultFilters.option);
	const [selectedGroup, setSelectedGroup] = useState(defaultFilters.group);

	const selectorEmissions = createSelector(
		state => state.FuelSupply,
		(_, type) => type,
		(data, type) => ({ info: data.emissionsSummary || null })
	);
	const { info: infoEmissions } = useSelector(state =>
		selectorEmissions(state, 'emissions')
	);

	const fetchEmissionsData = useCallback(
		(idEstablishment, filter, group) => {
			dispatch(
				getEmissionsSummary({
					establishment_id: idEstablishment,
					filter: filter,
					group: group,
				})
			);
		},
		[dispatch]
	);

	// Fetch data
	useEffect(() => {
		if (!establishmentId) return;
		fetchEmissionsData(establishmentId, selectedDate, selectedGroup);
	}, [establishmentId, selectedDate, selectedGroup, fetchEmissionsData]);

	const { series, categories, legendData } = useMemo(() => {
		if (!infoEmissions?.data)
			return { series: [], categories: [], legendData: [] };
		return {
			series: [
				{
					name: 'Total Emisiones',
					data: infoEmissions?.data?.map(item => item.total_emissions),
					type: 'bar',
				},
			],
			categories: infoEmissions?.data?.map(item => item.date),
			legendData: ['Total Emisiones'],
		};
	}, [infoEmissions?.data]);

	// const yAxisUnit = useMemo(() => {
	// 	return getUniqueUnits(infoEmissions?.data);
	// }, [infoEmissions?.data]);

	const options = useMemo(
		() => ({
			legend: { data: legendData, bottom: 10, top: undefined },
			xAxis: [{ type: 'category', data: categories }],
			yAxis: [{ type: 'value', name: 'kgCO2' }],
			series,
		}),
		[series, categories, legendData]
	);

	const noData = !infoEmissions?.data?.length;
	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	useEffect(() => {
		if (Array.isArray(infoEmissions?.data)) {
			setHasLoadedOnce(true);
		}
	}, [infoEmissions?.data]);

	const actions = (
		<div className='btn-box-dashboard' style={{ display: 'flex', gap: 8 }}>
			<Select
				placeholder='Agrupación'
				data={[
					{ label: 'Por Centro', value: 'centro' },
					{ label: 'Por Fuente', value: 'fuente' },
				]}
				value={selectedGroup}
				onChange={value => value && setSelectedGroup(value)}
				size='xs'
				radius='lg'
				styles={{ input: { width: 100 } }}
			/>
			<Select
				placeholder='Fecha'
				data={[
					{ label: 'Ciclo', value: 'ciclo' },
					{ label: 'Año', value: 'año' },
					{ label: 'Semestre', value: 'semestre' },
					{ label: 'Mes', value: 'mes' },
					{ label: 'Día', value: 'dia' },
				]}
				value={selectedDate}
				onChange={value => value && setSelectedDate(value)}
				size='xs'
				radius='lg'
				styles={{ input: { width: 100 } }}
			/>
		</div>
	);

	return (
		<OChartCard
			title={title}
			actions={actions}
			isLoading={!hasLoadedOnce && !infoEmissions?.data}
			error={null}
			isEmpty={noData}
			emptyIconClass='ri-gas-station-fill'
			emptyMessage='No cuenta con registro de consumo de combustible'
		>
			<OBarChart
				options={options}
				minHeight={minHeight}
				maxHeight={maxHeight}
			/>
		</OChartCard>
	);
};

export default EmissionsPowerGenerationEChart;
